import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    LinearProgress,
    Alert
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    Visibility,
    TrendingUp,
    People,
    Share,
    QrCodeScanner,
    Phone,
    Email,
    WhatsApp,
    LinkedIn,
    GitHub,
    Twitter,
    Instagram,
    Facebook,
    Download,
    TouchApp,
    DevicesOther,
    LocationOn,
    X as XIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { usePage } from '../context/PageContext';
import { useAuth } from '../context/AuthContext';

// Conditional import with fallback
let analyticsApiImports;
try {
    analyticsApiImports = require('../api/analyticsApi');
} catch (error) {
    console.error('Failed to import analytics API:', error);
    analyticsApiImports = {};
}

const { 
    getCards = () => Promise.resolve([]), 
    getCardAnalytics = () => Promise.resolve({}), 
    getUserAnalytics = () => Promise.resolve({}), 
    getAllUsers = () => Promise.resolve([]), 
    getUserCards = () => Promise.resolve([]), 
    getAllUsersAnalytics = () => Promise.resolve({}) 
} = analyticsApiImports;

// Mock data - In real app, this would come from API
const generateMockData = () => {
    const currentDate = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        return {
            date: date.toISOString().split('T')[0],
            views: Math.floor(Math.random() * 50) + 10,
            clicks: Math.floor(Math.random() * 20) + 5,
            shares: Math.floor(Math.random() * 8) + 1
        };
    }).reverse();

    return {
        overview: {
            totalViews: 1247,
            totalClicks: 342,
            totalShares: 89,
            conversionRate: 27.4
        },
        dailyStats: last7Days,
        topCountries: [
            { country: 'Singapore', views: 456, flag: '🇸🇬' },
            { country: 'India', views: 321, flag: '🇮🇳' },
            { country: 'Malaysia', views: 234, flag: '🇲🇾' },
            { country: 'Indonesia', views: 156, flag: '🇮🇩' },
            { country: 'Thailand', views: 80, flag: '🇹🇭' }
        ],
        deviceStats: [
            { device: 'Mobile', percentage: 68, count: 849 },
            { device: 'Desktop', percentage: 24, count: 299 },
            { device: 'Tablet', percentage: 8, count: 99 }
        ],
        contactActions: [
            { action: 'Phone Call', icon: <Phone />, count: 45, color: '#4caf50' },
            { action: 'Email', icon: <Email />, count: 67, color: '#1976d2' },
            { action: 'WhatsApp', icon: <WhatsApp />, count: 89, color: '#25d366' },
            { action: 'vCard Download', icon: <Download />, count: 123, color: '#9c27b0' }
        ],
        socialClicks: [
            { platform: 'LinkedIn', icon: <LinkedIn />, clicks: 156, color: '#0077b5' },
            { platform: 'GitHub', icon: <GitHub />, clicks: 89, color: '#333' },
            { platform: 'Twitter', icon: <Twitter />, clicks: 67, color: '#1da1f2' },
            { platform: 'Instagram', icon: <Instagram />, clicks: 45, color: '#e4405f' },
            { platform: 'Facebook', icon: <Facebook />, clicks: 34, color: '#1877f2' }
        ]
    };
};

const StatCard = ({ title, value, subtitle, icon, color, trend }) => {
    const theme = useTheme();
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -2 }}
        >
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
                            {icon}
                        </Avatar>
                        {trend && (
                            <Chip 
                                icon={<TrendingUp />} 
                                label={`+${trend}%`} 
                                color="success" 
                                size="small" 
                            />
                        )}
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {value}
                    </Typography>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
};

const DeviceStatsCard = ({ deviceStats }) => (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <DevicesOther sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Device Breakdown
                </Typography>
            </Box>
            {deviceStats.map((device, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                            {device.device}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {device.count} ({device.percentage}%)
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={device.percentage} 
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>
            ))}
        </CardContent>
    </Card>
);

const TopCountriesCard = ({ countries }) => (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOn sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Top Countries
                </Typography>
            </Box>
            {countries.map((country, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '1.2rem', mr: 2 }}>
                            {country.flag}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                            {country.country}
                        </Typography>
                    </Box>
                    <Chip label={country.views} color="primary" size="small" />
                </Box>
            ))}
        </CardContent>
    </Card>
);

const ContactActionsCard = ({ actions }) => (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TouchApp sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact Actions
                </Typography>
            </Box>
            <Grid container spacing={2}>
                {actions.map((action, index) => (
                    <Grid item xs={6} key={index}>
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: 'background.paper', 
                            borderRadius: 2, 
                            border: 1, 
                            borderColor: 'divider',
                            textAlign: 'center'
                        }}>
                            <Avatar sx={{ bgcolor: action.color, mx: 'auto', mb: 1, width: 40, height: 40 }}>
                                {action.icon}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: action.color }}>
                                {action.count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {action.action}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </CardContent>
    </Card>
);

const SocialClicksCard = ({ socialClicks }) => (
    <Card elevation={3} sx={{ borderRadius: 3 }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Share sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Social Media Clicks
                </Typography>
            </Box>
            {socialClicks.map((social, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: social.color, width: 32, height: 32, mr: 2 }}>
                            {social.icon}
                        </Avatar>
                        <Typography variant="body1" fontWeight="medium">
                            {social.platform}
                        </Typography>
                    </Box>
                    <Chip label={social.clicks} color="primary" size="small" />
                </Box>
            ))}
        </CardContent>
    </Card>
);

const AnalyticsPage = () => {
    const { setPageTitle } = usePage();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState('all');
    const [timeRange, setTimeRange] = useState('7days');
    
    // Admin-specific state
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [userCards, setUserCards] = useState([]);
    const isAdmin = user?.role === 'admin';
    

    
    // Convert time range to days
    const getDaysFromTimeRange = (range) => {
        switch (range) {
            case '7days': return 7;
            case '30days': return 30;
            case '90days': return 90;
            default: return 30;
        }
    };

    const fetchUsers = async () => {
        if (!isAdmin) return;
        
        try {
            const usersData = await getAllUsers();
            setUsers(usersData || []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setUsers([]);
        }
    };

    const fetchCards = async () => {
        try {
            const cardList = await getCards();
            setCards(cardList || []);
            
            // For admin users, set default to 'all', for regular users set to first card
            if (!selectedCard || selectedCard === 'all') {
                if (isAdmin) {
                    setSelectedCard('all');
                } else if (cardList && cardList.length > 0) {
                    setSelectedCard(cardList[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch cards:', error);
            setError('Failed to load cards');
            setCards([]);
        }
    };

    const fetchUserCards = async (userId) => {
        if (!isAdmin) return;
        
        try {
            const cardsData = await getUserCards(userId);
            setUserCards(cardsData || []);
        } catch (error) {
            console.error('Failed to fetch user cards:', error);
            setUserCards([]);
        }
    };

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const days = getDaysFromTimeRange(timeRange);
            
            let analyticsData;
            let transformedData;
            
            if (isAdmin) {
                // Admin analytics logic
                if (selectedUser === 'all' && selectedCard === 'all') {
                    // All users, all cards - global analytics
                    console.log(`[Analytics] Admin: Fetching global analytics, days: ${days}`);
                    analyticsData = await getAllUsersAnalytics(days);
                    transformedData = {
                        overview: {
                            totalViews: analyticsData.overview.total_views,
                            totalClicks: analyticsData.overview.total_interactions,
                            totalShares: analyticsData.overview.total_shares,
                            conversionRate: analyticsData.overview.conversion_rate,
                            uniqueVisitors: analyticsData.overview.unique_visitors
                        },
                        deviceStats: analyticsData.device_stats.map(device => ({
                            device: device.device.charAt(0).toUpperCase() + device.device.slice(1),
                            percentage: device.percentage,
                            count: device.count
                        })),
                        topCountries: analyticsData.country_stats.slice(0, 5).map(country => ({
                            country: country.country,
                            views: country.count,
                            flag: getCountryFlag(country.country)
                        })),
                        contactActions: transformInteractions(analyticsData.interaction_breakdown, 'contact'),
                        socialClicks: transformInteractions(analyticsData.interaction_breakdown, 'social'),
                        viewType: 'global'
                    };
                } else if (selectedUser !== 'all' && selectedCard === 'all') {
                    // Specific user, all their cards
                    console.log(`[Analytics] Admin: Fetching user analytics for user ID: ${selectedUser}, days: ${days}`);
                    analyticsData = await getUserAnalytics(selectedUser, days);
                    const userData = users.find(u => u.id === parseInt(selectedUser));
                    transformedData = {
                        overview: {
                            totalViews: analyticsData.overview.total_views,
                            totalClicks: analyticsData.overview.total_interactions,
                            totalShares: analyticsData.overview.total_shares,
                            conversionRate: analyticsData.overview.conversion_rate,
                            uniqueVisitors: analyticsData.overview.unique_visitors
                        },
                        deviceStats: analyticsData.device_stats.map(device => ({
                            device: device.device.charAt(0).toUpperCase() + device.device.slice(1),
                            percentage: device.percentage,
                            count: device.count
                        })),
                        topCountries: analyticsData.country_stats.slice(0, 5).map(country => ({
                            country: country.country,
                            views: country.count,
                            flag: getCountryFlag(country.country)
                        })),
                        contactActions: transformInteractions(analyticsData.interaction_breakdown, 'contact'),
                        socialClicks: transformInteractions(analyticsData.interaction_breakdown, 'social'),
                        selectedUser: userData,
                        viewType: 'user'
                    };
                } else {
                    // Specific card selected
                    console.log(`[Analytics] Admin: Fetching card analytics for card ID: ${selectedCard}, days: ${days}`);
                    analyticsData = await getCardAnalytics(selectedCard, days);
                    
                    // Find card info from either main cards or user cards
                    const cardInfo = cards.find(c => c.id === selectedCard) || userCards.find(c => c.id === selectedCard);
                    const userData = users.find(u => u.id === parseInt(selectedUser));
                    
                    transformedData = {
                        overview: {
                            totalViews: analyticsData.overview.total_views,
                            totalClicks: analyticsData.overview.total_interactions,
                            totalShares: analyticsData.overview.total_shares,
                            conversionRate: analyticsData.overview.conversion_rate,
                            uniqueVisitors: analyticsData.overview.unique_visitors
                        },
                        deviceStats: analyticsData.device_stats.map(device => ({
                            device: device.device.charAt(0).toUpperCase() + device.device.slice(1),
                            percentage: device.percentage,
                            count: device.count
                        })),
                        topCountries: analyticsData.country_stats.slice(0, 5).map(country => ({
                            country: country.country,
                            views: country.count,
                            flag: getCountryFlag(country.country)
                        })),
                        contactActions: transformInteractions(analyticsData.interaction_breakdown, 'contact'),
                        socialClicks: transformInteractions(analyticsData.interaction_breakdown, 'social'),
                        selectedCard: cardInfo,
                        selectedUser: userData,
                        viewType: 'card'
                    };
                }
            } else {
                // Regular user analytics logic
                if (selectedCard !== 'all') {
                    // Specific card selected - show that card's analytics
                    console.log(`[Analytics] Fetching card analytics for card ID: ${selectedCard}, days: ${days}`);
                    analyticsData = await getCardAnalytics(selectedCard, days);
                    
                    const cardInfo = cards.find(c => c.id === selectedCard);
                    transformedData = {
                        overview: {
                            totalViews: analyticsData.overview.total_views,
                            totalClicks: analyticsData.overview.total_interactions,
                            totalShares: analyticsData.overview.total_shares,
                            conversionRate: analyticsData.overview.conversion_rate,
                            uniqueVisitors: analyticsData.overview.unique_visitors
                        },
                        deviceStats: analyticsData.device_stats.map(device => ({
                            device: device.device.charAt(0).toUpperCase() + device.device.slice(1),
                            percentage: device.percentage,
                            count: device.count
                        })),
                        topCountries: analyticsData.country_stats.slice(0, 5).map(country => ({
                            country: country.country,
                            views: country.count,
                            flag: getCountryFlag(country.country)
                        })),
                        contactActions: transformInteractions(analyticsData.interaction_breakdown, 'contact'),
                        socialClicks: transformInteractions(analyticsData.interaction_breakdown, 'social'),
                        selectedCard: cardInfo,
                        viewType: 'card'
                    };
                } else {
                    // "All" cards selected - show user's combined analytics
                    if (cards.length > 0) {
                        console.log(`[Analytics] Fetching user's own analytics, days: ${days}`);
                        analyticsData = await getUserAnalytics(user.id, days);
                        
                        transformedData = {
                            overview: {
                                totalViews: analyticsData.overview.total_views,
                                totalClicks: analyticsData.overview.total_interactions,
                                totalShares: analyticsData.overview.total_shares,
                                conversionRate: analyticsData.overview.conversion_rate,
                                uniqueVisitors: analyticsData.overview.unique_visitors
                            },
                            deviceStats: analyticsData.device_stats.map(device => ({
                                device: device.device.charAt(0).toUpperCase() + device.device.slice(1),
                                percentage: device.percentage,
                                count: device.count
                            })),
                            topCountries: analyticsData.country_stats.slice(0, 5).map(country => ({
                                country: country.country,
                                views: country.count,
                                flag: getCountryFlag(country.country)
                            })),
                            contactActions: transformInteractions(analyticsData.interaction_breakdown, 'contact'),
                            socialClicks: transformInteractions(analyticsData.interaction_breakdown, 'social'),
                            viewType: 'user'
                        };
                    } else {
                        setData(null);
                        setLoading(false);
                        return;
                    }
                }
            }
            
            setData(transformedData);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch analytics:', err);
            setError('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    const getCountryFlag = (countryName) => {
        const countryFlags = {
            'Singapore': '🇸🇬',
            'India': '🇮🇳',
            'Malaysia': '🇲🇾',
            'Indonesia': '🇮🇩',
            'Thailand': '🇹🇭',
            'Philippines': '🇵🇭',
            'United States': '🇺🇸',
            'United Kingdom': '🇬🇧',
            'Australia': '🇦🇺',
            'Canada': '🇨🇦'
        };
        return countryFlags[countryName] || '🌍';
    };

    const transformInteractions = (interactions, type) => {
        const contactTypes = ['email', 'phone', 'whatsapp', 'vcard_download'];
        const socialTypes = ['linkedin', 'github', 'twitter', 'x', 'instagram', 'facebook'];
        
        // Filter interactions based on type and normalize them
        const relevantInteractions = interactions.filter(interaction => {
            const interactionType = interaction.type?.toLowerCase() || '';
            const interactionValue = interaction.value?.toLowerCase() || '';
            
            if (type === 'contact') {
                return contactTypes.some(ct => 
                    interactionType.includes(ct) || 
                    interactionValue.includes(ct)
                );
            } else {
                return socialTypes.some(st => 
                    interactionType.includes(st) || 
                    interactionValue.includes(st) ||
                    interactionType === 'social_click' ||
                    interactionType === 'social_media_click'
                );
            }
        });

        if (type === 'contact') {
            return [
                { action: 'Phone Call', icon: <Phone />, count: getInteractionCount(relevantInteractions, 'phone'), color: '#4caf50' },
                { action: 'Email', icon: <Email />, count: getInteractionCount(relevantInteractions, 'email'), color: '#1976d2' },
                { action: 'WhatsApp', icon: <WhatsApp />, count: getInteractionCount(relevantInteractions, 'whatsapp'), color: '#25d366' },
                { action: 'vCard Download', icon: <Download />, count: getInteractionCount(relevantInteractions, 'vcard'), color: '#9c27b0' }
            ];
        } else {
            return [
                { platform: 'LinkedIn', icon: <LinkedIn />, clicks: getInteractionCount(relevantInteractions, 'linkedin'), color: '#0077b5' },
                { platform: 'GitHub', icon: <GitHub />, clicks: getInteractionCount(relevantInteractions, 'github'), color: '#333' },
                { platform: 'Twitter/X', icon: <XIcon />, clicks: getInteractionCount(relevantInteractions, ['twitter', 'x']), color: '#000' },
                { platform: 'Instagram', icon: <Instagram />, clicks: getInteractionCount(relevantInteractions, 'instagram'), color: '#e4405f' },
                { platform: 'Facebook', icon: <Facebook />, clicks: getInteractionCount(relevantInteractions, 'facebook'), color: '#1877f2' }
            ];
        }
    };

    const getInteractionCount = (interactions, typeOrTypes) => {
        const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
        return interactions
            .filter(interaction => {
                const interactionType = interaction.type?.toLowerCase() || '';
                const interactionValue = interaction.value?.toLowerCase() || '';
                
                // Check if it's a social media click with a specific platform
                if (interactionType === 'social_click' || interactionType === 'social_media_click') {
                    return types.some(type => interactionValue.includes(type));
                }
                
                // Check direct type/value matches
                return types.some(type => 
                    interactionType.includes(type) || 
                    interactionValue.includes(type)
                );
            })
            .reduce((sum, interaction) => sum + (interaction.count || 1), 0);
    };

    const handleUserChange = (e) => {
        const newUser = e.target.value;
        setSelectedUser(newUser);
        setSelectedCard('all'); // Reset card selection when user changes
        
        if (newUser !== 'all') {
            fetchUserCards(newUser);
        } else {
            setUserCards([]);
        }
    };

    const getCardDropdownLabel = () => {
        if (!isAdmin) return 'All My Cards';
        
        if (selectedUser === 'all') {
            return 'All Cards (All Users)';
        } else {
            const userData = users.find(u => u.id === parseInt(selectedUser));
            return userData ? `All Cards (${userData.username})` : 'All Cards (Selected User)';
        }
    };

    const getCardDropdownOptions = () => {
        if (!isAdmin) {
            return cards;
        }
        
        if (selectedUser === 'all') {
            return cards; // All cards from all users
        } else {
            return userCards; // Cards from selected user
        }
    };

    const getAnalyticsTitle = () => {
        if (!isAdmin) {
            return data?.viewType === 'card' && data?.selectedCard ? 
                `Analytics for "${data.selectedCard.name}" card` : 
                'Combined analytics for all your cards';
        }
        
        if (data?.viewType === 'global') {
            return 'Global analytics for all users and cards';
        } else if (data?.viewType === 'user' && data?.selectedUser) {
            return `Combined analytics for ${data.selectedUser.username}'s cards`;
        } else if (data?.viewType === 'card' && data?.selectedCard) {
            const ownerInfo = data.selectedUser ? ` (${data.selectedUser.username})` : '';
            return `Analytics for "${data.selectedCard.name}" card${ownerInfo}`;
        }
        
        return 'Analytics';
    };

    useEffect(() => {
        setPageTitle('Analytics');
        fetchCards();
        if (isAdmin) {
            fetchUsers();
        }
    }, [setPageTitle, isAdmin]);

    useEffect(() => {
        fetchAnalytics();
    }, [selectedCard, selectedUser, timeRange, cards, userCards]);



    // Fallback Content
    const FallbackContent = () => (
        <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
                Analytics data is not available. This could be due to:
                <ul>
                    <li>Backend server not running</li>
                    <li>No cards created yet</li>
                    <li>Network connectivity issues</li>
                    <li>Authentication problems</li>
                </ul>
            </Alert>
            
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Views"
                        value="0"
                        subtitle="Card visits"
                        icon={<Visibility />}
                        color="#2196f3"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Unique Visitors"
                        value="0"
                        subtitle="Unique IP addresses"
                        icon={<People />}
                        color="#4caf50"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Total Interactions"
                        value="0"
                        subtitle="Contact & social clicks"
                        icon={<TouchApp />}
                        color="#ff9800"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Conversion Rate"
                        value="0%"
                        subtitle="Viewers to actions"
                        icon={<TrendingUp />}
                        color="#e91e63"
                    />
                </Grid>
            </Grid>
        </Box>
    );

    // Always show the page, even if there are errors
    const showFallback = !data && !loading;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <Box>

                
                {/* Loading Indicator */}
                {loading && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Loading analytics data...
                    </Alert>
                )}
                
                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Error: {error}
                    </Alert>
                )}
                
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    mb: 4, 
                    flexWrap: 'wrap', 
                    gap: 2 
                }}>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 'bold', 
                            mb: 1,
                            fontSize: { xs: '1.2rem', sm: '1.8rem' }
                        }}>
                            Card Analytics {loading && '(Loading...)'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {showFallback ? 'Analytics data unavailable' : (data ? getAnalyticsTitle() : 'Preparing analytics...')}
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        alignItems: 'center', 
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        {/* User Selection (Admin only) */}
                        {isAdmin && (
                            <FormControl size="small" sx={{ 
                                minWidth: { xs: '100%', sm: 200 },
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                <InputLabel>User Selection</InputLabel>
                                <Select
                                    value={selectedUser}
                                    onChange={handleUserChange}
                                    label="User Selection"
                                >
                                    <MenuItem value="all">
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            All Users
                                        </Typography>
                                    </MenuItem>
                                    {users.map((userData) => (
                                        <MenuItem key={userData.id} value={userData.id}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography>{userData.username}</Typography>
                                                    {userData.role === 'admin' && (
                                                        <Chip label="Admin" size="small" color="primary" />
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {userData.card_count} cards
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        
                        {/* Card Selection */}
                        <FormControl size="small" sx={{ 
                            minWidth: { xs: '100%', sm: 200 },
                            width: { xs: '100%', sm: 'auto' }
                        }}>
                            <InputLabel>Select Card</InputLabel>
                            <Select
                                value={selectedCard}
                                onChange={(e) => {
                                    setSelectedCard(e.target.value);
                                }}
                                label="Select Card"
                            >
                                {/* Show "All Cards" option only for admin users */}
                                {isAdmin && (
                                    <MenuItem value="all">
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            {getCardDropdownLabel()}
                                        </Typography>
                                    </MenuItem>
                                )}
                                {getCardDropdownOptions().map((card) => (
                                    <MenuItem key={card.id} value={card.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography>{card.name}</Typography>
                                                {!card.is_published && (
                                                    <Chip label="Draft" size="small" color="warning" />
                                                )}
                                            </Box>
                                            {isAdmin && card.owner && (
                                                <Typography variant="caption" color="text.secondary">
                                                    by {card.owner.username}
                                                </Typography>
                                            )}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        {/* Time Range Selection */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Time Range</InputLabel>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                label="Time Range"
                            >
                                <MenuItem value="7days">Last 7 Days</MenuItem>
                                <MenuItem value="30days">Last 30 Days</MenuItem>
                                <MenuItem value="90days">Last 3 Months</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Main Content Area */}
                {showFallback ? (
                    <FallbackContent />
                ) : data ? (
                    <>
                        {/* Overview Stats */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <StatCard
                                    title="Total Views"
                                    value={data.overview.totalViews.toLocaleString()}
                                    subtitle="Card visits"
                                    icon={<Visibility />}
                                    color="#2196f3"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <StatCard
                                    title="Unique Visitors"
                                    value={data.overview.uniqueVisitors.toLocaleString()}
                                    subtitle="Unique IP addresses"
                                    icon={<People />}
                                    color="#4caf50"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <StatCard
                                    title="Total Interactions"
                                    value={data.overview.totalClicks.toLocaleString()}
                                    subtitle="Contact & social clicks"
                                    icon={<TouchApp />}
                                    color="#ff9800"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <StatCard
                                    title="Card Shares"
                                    value={data.overview.totalShares.toLocaleString()}
                                    subtitle="QR code scans"
                                    icon={<QrCodeScanner />}
                                    color="#9c27b0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.4}>
                                <StatCard
                                    title="Conversion Rate"
                                    value={`${data.overview.conversionRate}%`}
                                    subtitle="Viewers to actions"
                                    icon={<TrendingUp />}
                                    color="#e91e63"
                                />
                            </Grid>
                        </Grid>

                        {/* Analytics Grid */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={6}>
                                <DeviceStatsCard deviceStats={data.deviceStats} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TopCountriesCard countries={data.topCountries} />
                            </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <ContactActionsCard actions={data.contactActions} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SocialClicksCard socialClicks={data.socialClicks} />
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <Typography>Initializing analytics...</Typography>
                    </Box>
                )}
            </Box>
        </motion.div>
    );
};

export default AnalyticsPage; 